import { Button } from "../components/Button";
import { Icon } from "../components/Icon";
import { Spinner } from "../components/Spinner";
import { TooltipContent, TooltipPortal, TooltipProvider, TooltipRoot, TooltipTrigger, } from "../components/Tooltip";
import { ChevronDown, ChevronRight } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import React, { useCallback, useEffect, useState, } from "react";
import { Checkbox } from "./Checkbox";
/**
 * A hierarchical, expandable list wrapping a set of `Tree.Item` nodes, each taking a
 * `label`, optional `visual` icon, and nested children. Use it to browse nested
 * structures such as folders, data sources, or document hierarchies; for lazily loaded
 * branches, render a child `<Tree isLoading />` until data arrives rather than blocking
 * the whole tree.
 *
 * @summary Hierarchical expandable list.
 */
export function Tree({ children, isLoading, isBoxed = false, overflowVisible = false, tailwindIconTextColor, variant = "finder", className, }) {
    const modifiedChildren = React.Children.map(children, (child) => {
        // /!\ Limitation: This stops on the first invalid element.
        // Meaning that if Tree.Item is not the first child, it will not work.
        if (React.isValidElement(child)) {
            // Clone the child element and pass the necessary props
            const childProps = {};
            if (variant === "navigator") {
                childProps.isNavigatable = true;
            }
            if (tailwindIconTextColor) {
                childProps.tailwindIconTextColor = tailwindIconTextColor;
            }
            return React.cloneElement(child, childProps);
        }
        return child;
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: cn("flex flex-col gap-0.5", overflowVisible ? "overflow-visible" : "overflow-hidden", isBoxed &&
                "rounded-xl border border-border bg-muted-background px-3 py-2", className) },
            React.createElement("div", null, modifiedChildren),
            isLoading && (React.createElement("div", { className: cn("flex justify-center py-2") },
                React.createElement(Spinner, { size: "xs" }))))));
}
const treeItemStyleClasses = {
    base: "group/tree flex cursor-default flex-row items-center gap-2 h-9",
    isNavigatableBase: "rounded-xl pl-1.5 pr-3 cursor-pointer transition-colors duration-150 motion-reduce:transition-none",
    isNavigatableUnselected: cn("bg-hover/0", "hover:bg-hover"),
    isNavigatableSelected: cn("font-medium", "bg-selected"),
};
/**
 * One row of a `Tree`: a chevron (for `type="node"`), optional `checkbox`, `visual`
 * icon, truncating `label` with an automatic tooltip, and trailing `actions`. Nested
 * items go in `children`, or in `renderTreeItems` to defer rendering until expanded.
 *
 * @summary Single tree row.
 */
Tree.Item = React.forwardRef(({ label, type = "node", className = "", labelClassName = "", tailwindIconTextColor = "text-muted-foreground", visual, checkbox, onChevronClick, collapsed, defaultCollapsed, actions, areActionsFading = true, renderTreeItems, children, isNavigatable = false, isSelected = false, onItemClick, id, }, ref) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const labelRef = React.useRef(null);
    const [collapsedState, setCollapsedState] = useState(defaultCollapsed !== null && defaultCollapsed !== void 0 ? defaultCollapsed : true);
    const isControlledCollapse = collapsed !== undefined;
    const effectiveCollapsed = isControlledCollapse
        ? collapsed
        : collapsedState;
    const effectiveOnChevronClick = isControlledCollapse
        ? onChevronClick
        : () => setCollapsedState(!collapsedState);
    const canExpand = effectiveOnChevronClick && type === "node";
    const getChildren = () => {
        if (effectiveCollapsed) {
            return [];
        }
        return typeof renderTreeItems === "function"
            ? renderTreeItems()
            : children;
    };
    const childrenToRender = getChildren();
    const checkTruncation = useCallback(() => {
        if (labelRef.current) {
            setIsTruncated(labelRef.current.scrollWidth > labelRef.current.clientWidth);
        }
    }, []);
    useEffect(() => {
        const observer = new ResizeObserver(checkTruncation);
        if (labelRef.current) {
            observer.observe(labelRef.current);
            checkTruncation();
        }
        return () => observer.disconnect();
    }, [checkTruncation]);
    const isExpanded = childrenToRender && !effectiveCollapsed;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: ref, id: id, className: cn(treeItemStyleClasses.base, onItemClick || (checkbox === null || checkbox === void 0 ? void 0 : checkbox.onCheckedChange) || canExpand
                ? "cursor-pointer"
                : "", isNavigatable ? treeItemStyleClasses.isNavigatableBase : "", isNavigatable
                ? isSelected
                    ? treeItemStyleClasses.isNavigatableSelected
                    : treeItemStyleClasses.isNavigatableUnselected
                : "", isExpanded ? "is-expanded" : "is-collapsed", type, className), onClick: onItemClick
                ? (e) => {
                    if (e.target instanceof HTMLElement &&
                        (e.target.closest('[role="checkbox"]') ||
                            e.target.tagName === "BUTTON")) {
                        return;
                    }
                    e.stopPropagation();
                    onItemClick();
                }
                : (e) => {
                    var _a;
                    if (!(e.target instanceof HTMLElement)) {
                        return;
                    }
                    if (e.target.tagName === "BUTTON" ||
                        e.target.closest('[role="checkbox"]')) {
                        return;
                    }
                    e.stopPropagation();
                    if (checkbox === null || checkbox === void 0 ? void 0 : checkbox.onCheckedChange) {
                        (_a = checkbox.onCheckedChange) === null || _a === void 0 ? void 0 : _a.call(checkbox, !checkbox.checked);
                    }
                    else if (canExpand) {
                        effectiveOnChevronClick();
                    }
                } },
            type === "node" && (React.createElement(Button, { icon: isExpanded ? ChevronDown : ChevronRight, size: "xmini", variant: "ghost-secondary", disabled: !effectiveOnChevronClick, onClick: (e) => {
                    e.stopPropagation();
                    if (effectiveOnChevronClick) {
                        effectiveOnChevronClick();
                    }
                } })),
            type === "leaf" && React.createElement("div", { className: "w-[24px] flex-shrink-0" }),
            checkbox && React.createElement(Checkbox, { ...checkbox }),
            React.createElement(Icon, { visual: visual, size: "sm", className: tailwindIconTextColor }),
            isTruncated ? (React.createElement(TooltipProvider, null,
                React.createElement(TooltipRoot, null,
                    React.createElement(TooltipTrigger, { asChild: true },
                        React.createElement("div", { ref: labelRef, className: cn("font-medium truncate text-sm text-primary", labelClassName) }, label)),
                    React.createElement(TooltipPortal, null,
                        React.createElement(TooltipContent, { side: "top", align: "start" }, label))))) : (React.createElement("div", { ref: labelRef, className: cn("font-medium truncate text-sm text-primary", labelClassName) }, label)),
            actions && (React.createElement("div", { className: cn("flex grow gap-2", areActionsFading &&
                    "transform opacity-0 duration-300 group-hover/tree:opacity-100") }, actions))),
        React.Children.count(childrenToRender) > 0 && (React.createElement("div", { className: "pl-4" }, childrenToRender))));
});
Tree.Empty = function ({ label, onItemClick }) {
    return (React.createElement("div", { className: cn("copy-sm py-1.5 pl-6 italic", "text-muted-foreground", onItemClick ? "cursor-pointer" : ""), onClick: onItemClick }, label));
};
//# sourceMappingURL=Tree.js.map